import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

/**
 * Where the map of scroll positions lives. `sessionStorage`, not `localStorage`: a position is only
 * meaningful for the current tab session — restoring yesterday's offset on a fresh visit would be
 * surprising, and the entries would accumulate forever.
 */
const STORAGE_KEY = 'sav:scroll-positions';

/**
 * Cap on remembered pages. Entries are dropped oldest-first, so a long browsing session keeps the
 * recent pages (the ones a Back button can actually reach) without growing without bound.
 */
const MAX_ENTRIES = 60;

/**
 * How long to keep re-applying a restore, in ms. Pages fetch their content after navigation ends,
 * so the document is short at first and a single `scrollTo` clamps to the wrong offset. We keep
 * re-applying each frame until the page has grown tall enough to reach the target.
 */
const RESTORE_BUDGET_MS = 3000;

/** Tolerance in px for "we reached the target" — sub-pixel layout makes exact equality flaky. */
const RESTORE_EPSILON = 2;

/**
 * Restores the scroll position on reload and on Back/Forward, and scrolls to top on a forward
 * navigation.
 *
 * Angular's own `withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })` cannot do this,
 * for two reasons this service exists to fix:
 *
 * 1. **Reload.** Enabling it makes the router set `history.scrollRestoration = 'manual'`, which
 *    switches OFF the browser's native reload restoration — and the router's own store is a plain
 *    in-memory object that dies with the page. So a refresh always landed at the top. We persist to
 *    `sessionStorage` instead, which survives the reload.
 *
 * 2. **Async content.** The router restores the offset the instant navigation ends, but pages load
 *    their data over HTTP afterwards. At that moment the document is only a screen or two tall, so
 *    the browser clamps a request for y=4000 down to the current maximum and the position is lost
 *    for good. We re-apply across frames until the content has arrived (see `RESTORE_BUDGET_MS`).
 *
 * Because this service owns the behaviour end to end, the router is configured with
 * `scrollPositionRestoration: 'disabled'` so the two never fight. `anchorScrolling` stays enabled —
 * that is a separate concern and still handled by the router.
 */
@Injectable({ providedIn: 'root' })
export class ScrollRestorationService {
  private readonly router = inject(Router);
  private readonly doc = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private positions = new Map<string, number>();
  /** Key of the page currently on screen — what a scroll event should be recorded against. */
  private currentKey = '';
  /** True until the first `NavigationEnd`, which needs different handling (reload vs fresh visit). */
  private isFirstNavigation = true;
  /** Set when the in-flight navigation came from the Back/Forward buttons. */
  private isPopstateNavigation = false;
  /** Cancels an in-flight restore loop when a newer one starts. */
  private restoreToken = 0;
  private savePending = false;
  /**
   * Offset to restore once the first navigation completes, read during `init()`.
   *
   * It has to be captured that early: a reload begins at the top, and both the initial
   * `NavigationStart` and the browser's own scroll reset record a position of 0 against this URL —
   * overwriting the stored offset before there is any chance to use it.
   */
  private pendingRestore: number | null = null;
  /** Blocks recording while a restore is in flight, for the same reason. */
  private suspendSaves = false;

  init(): void {
    if (!this.isBrowser) return;

    const win = this.doc.defaultView;
    if (!win) return;

    // We own restoration completely, so stop the browser from also trying — its attempt runs before
    // the page's data arrives and would land at the wrong offset, fighting our retry loop.
    if ('scrollRestoration' in win.history) {
      win.history.scrollRestoration = 'manual';
    }

    this.positions = this.read();
    this.currentKey = this.keyFromLocation();

    // Read the target before any listener can overwrite it (see `pendingRestore`).
    if (this.isReentry()) {
      const saved = this.positions.get(this.currentKey) ?? 0;
      if (saved > 0) {
        this.pendingRestore = saved;
        this.suspendSaves = true;
      }
    }

    // Passive + rAF-coalesced: this fires constantly while scrolling, so it must never do layout
    // work or block the scroll thread. Writing to sessionStorage is deferred to one write per frame.
    win.addEventListener('scroll', () => this.queueSave(), { passive: true });

    // `pagehide` rather than `beforeunload`: it also fires when a mobile browser puts the page into
    // the back/forward cache, which `beforeunload` does not.
    win.addEventListener('pagehide', () => {
      this.record();
      this.write();
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        // Save where we are before the outgoing page is torn down — but not on the very first
        // navigation, where there is no outgoing page and the current offset is just the top.
        if (!this.isFirstNavigation) {
          this.record();
          this.write();
        }
        this.isPopstateNavigation = event.navigationTrigger === 'popstate';
        return;
      }

      if (event instanceof NavigationEnd) {
        this.onNavigationEnd(event);
      }
    });
  }

  private onNavigationEnd(event: NavigationEnd): void {
    const key = this.keyFromLocation();
    const previousKey = this.currentKey;
    this.currentKey = key;

    // A URL fragment is the router's job (`anchorScrolling`), so leave that navigation alone
    // instead of racing it to a different offset.
    if (this.router.parseUrl(event.urlAfterRedirects).fragment) {
      this.isFirstNavigation = false;
      this.isPopstateNavigation = false;
      return;
    }

    if (this.isFirstNavigation) {
      this.isFirstNavigation = false;
      // Captured in `init()` for a reload or a Back that re-entered the app from outside it. A
      // genuinely fresh visit leaves it null and starts at the top on its own.
      if (this.pendingRestore !== null) {
        const target = this.pendingRestore;
        this.pendingRestore = null;
        this.restore(target);
      } else {
        this.suspendSaves = false;
      }
      return;
    }

    if (this.isPopstateNavigation) {
      this.isPopstateNavigation = false;
      this.restore(this.positions.get(key) ?? 0);
      return;
    }

    // Forward navigation to a different page starts at the top — the router used to do this, and
    // with its restoration disabled it is now ours. Same-page navigations (a query-param change on
    // the page you are already reading) keep their offset.
    if (key !== previousKey) {
      this.restore(0);
    }
  }

  /** True when this document was reloaded or reached with Back/Forward, rather than opened fresh. */
  private isReentry(): boolean {
    const win = this.doc.defaultView;
    if (!win?.performance?.getEntriesByType) return false;

    const [entry] = win.performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return entry?.type === 'reload' || entry?.type === 'back_forward';
  }

  /**
   * Applies a target offset, re-applying each frame until the page has grown tall enough to reach
   * it or the budget runs out. Aborts as soon as the reader scrolls themselves — chasing a stored
   * offset while someone is actively scrolling is worse than simply losing the position.
   */
  private restore(target: number): void {
    const win = this.doc.defaultView;
    if (!win) return;

    const token = ++this.restoreToken;

    if (target <= 0) {
      win.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
      this.suspendSaves = false;
      return;
    }

    // Nothing may be recorded mid-flight: the loop passes through intermediate offsets, and if it
    // were interrupted one of those would be stored as this page's position.
    this.suspendSaves = true;

    let cancelled = false;
    const cancel = () => {
      cancelled = true;
    };
    const listenerOptions = { passive: true, once: true } as const;
    win.addEventListener('wheel', cancel, listenerOptions);
    win.addEventListener('touchstart', cancel, listenerOptions);
    win.addEventListener('keydown', cancel, listenerOptions);

    const cleanup = () => {
      win.removeEventListener('wheel', cancel);
      win.removeEventListener('touchstart', cancel);
      win.removeEventListener('keydown', cancel);
      // Only the newest restore may lift the block, or an older loop finishing late would let a
      // stale position through while the current one is still settling.
      if (token === this.restoreToken) {
        this.suspendSaves = false;
        this.record();
        this.write();
      }
    };

    const startedAt = win.performance.now();

    const step = () => {
      // A newer navigation started, or the reader took over.
      if (cancelled || token !== this.restoreToken) {
        cleanup();
        return;
      }

      const maxScroll = Math.max(
        0,
        this.doc.documentElement.scrollHeight - win.innerHeight,
      );
      win.scrollTo({
        top: Math.min(target, maxScroll),
        left: 0,
        behavior: 'instant' as ScrollBehavior,
      });

      const reached = Math.abs(win.scrollY - target) <= RESTORE_EPSILON;
      if (reached || win.performance.now() - startedAt > RESTORE_BUDGET_MS) {
        cleanup();
        return;
      }

      win.requestAnimationFrame(step);
    };

    win.requestAnimationFrame(step);
  }

  /** Coalesces the scroll handler down to one recorded position per frame. */
  private queueSave(): void {
    if (this.savePending) return;
    this.savePending = true;

    this.doc.defaultView?.requestAnimationFrame(() => {
      this.savePending = false;
      this.record();
      this.write();
    });
  }

  /** Stores the current offset against the page on screen, re-inserting to keep the map in LRU order. */
  private record(): void {
    const win = this.doc.defaultView;
    if (!win || !this.currentKey || this.suspendSaves) return;

    this.positions.delete(this.currentKey);
    this.positions.set(this.currentKey, win.scrollY);

    while (this.positions.size > MAX_ENTRIES) {
      const oldest = this.positions.keys().next().value;
      if (oldest === undefined) break;
      this.positions.delete(oldest);
    }
  }

  /** Path + query, so `?page=2` is remembered separately from `?page=1`. The hash is excluded. */
  private keyFromLocation(): string {
    const location = this.doc.defaultView?.location;
    return location ? location.pathname + location.search : '';
  }

  private read(): Map<string, number> {
    try {
      const raw = this.doc.defaultView?.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return new Map();

      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Map();

      return new Map(
        parsed.filter(
          (entry): entry is [string, number] =>
            Array.isArray(entry) &&
            entry.length === 2 &&
            typeof entry[0] === 'string' &&
            typeof entry[1] === 'number',
        ),
      );
    } catch {
      // Private-mode Safari and storage-blocked contexts throw on access. Losing the position is
      // acceptable; breaking navigation is not.
      return new Map();
    }
  }

  private write(): void {
    try {
      this.doc.defaultView?.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify([...this.positions]),
      );
    } catch {
      // As above — storage may be unavailable or full.
    }
  }
}
